import { computed, onMounted, onUnmounted, ref } from 'vue';

/**
 * 断点配置（与 breakpoints.css 保持一致）
 */
export const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600,
} as const;

export type BreakpointKey = keyof typeof breakpoints;

/**
 * 检测是否为移动端
 */
export function isMobile(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < breakpoints.md;
}

/**
 * 检测是否为平板
 */
export function isTablet(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= breakpoints.md && window.innerWidth < breakpoints.lg;
}

/**
 * 检测是否为桌面端
 */
export function isDesktop(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= breakpoints.lg;
}

/**
 * 检测是否为触摸设备
 */
export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

/**
 * 根据 User-Agent 推断设备类型（用于服务端 SSR）
 */
export function getDeviceFromUA(userAgent: string): {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  breakpoint: BreakpointKey;
  width: number;
} {
  const ua = userAgent || '';
  // 平板：iPad、Android 平板等（Android 且无 Mobile 多为平板）
  const isTablet =
    /iPad|Tablet|PlayBook|Silk/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua));
  // 手机：常见移动端 UA，且排除已识别的平板
  const isMobile =
    !isTablet && /Mobile|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|webOS/i.test(ua);
  const isDesktop = !isMobile && !isTablet;

  let breakpoint: BreakpointKey;
  let width: number;
  if (isMobile) {
    breakpoint = 'xs';
    width = 375;
  } else if (isTablet) {
    breakpoint = 'md';
    width = 768;
  } else {
    breakpoint = 'lg';
    width = 1200;
  }

  return { isMobile, isTablet, isDesktop, breakpoint, width };
}

/**
 * 获取当前断点
 */
export function getCurrentBreakpoint(): BreakpointKey {
  if (typeof window === 'undefined') return 'xs';

  const width = window.innerWidth;

  if (width >= breakpoints.xxl) return 'xxl';
  if (width >= breakpoints.xl) return 'xl';
  if (width >= breakpoints.lg) return 'lg';
  if (width >= breakpoints.md) return 'md';
  if (width >= breakpoints.sm) return 'sm';
  return 'xs';
}

/**
 * 响应式断点 Hook
 *
 * 服务端渲染（SSR）时可根据传入的 userAgent 推断设备类型，避免首屏与客户端不一致。
 * 在 Nuxt 中可传入：useRequestHeaders()['user-agent']
 *
 * @param userAgent - 可选，请求头中的 User-Agent（SSR 时传入）
 *
 * @example
 * ```vue
 * <script setup>
 * import { useBreakpoint } from '@rendora/design/utils/responsive';
 *
 * // 客户端或未传 UA 时按窗口宽度计算；SSR 时传入 UA 则按 UA 推断
 * const headers = useRequestHeaders?.();
 * const { width, isMobile, isTablet, isDesktop, breakpoint } = useBreakpoint(headers?.['user-agent']);
 * </script>
 * ```
 */
export function useBreakpoint(userAgent?: string) {
  const isServer = typeof window === 'undefined';
  const initialWidth = isServer ? getDeviceFromUA(userAgent || '').width : window.innerWidth;

  const width = ref(initialWidth);

  const updateWidth = () => {
    if (typeof window !== 'undefined') {
      width.value = window.innerWidth;
    }
  };

  onMounted(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateWidth);
      updateWidth();
    }
  });

  onUnmounted(() => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', updateWidth);
    }
  });

  const isMobile = computed(() => width.value < breakpoints.md);
  const isTablet = computed(() => width.value >= breakpoints.md && width.value < breakpoints.lg);
  const isDesktop = computed(() => width.value >= breakpoints.lg);

  const breakpoint = computed<BreakpointKey>(() => {
    const w = width.value;
    if (w >= breakpoints.xxl) return 'xxl';
    if (w >= breakpoints.xl) return 'xl';
    if (w >= breakpoints.lg) return 'lg';
    if (w >= breakpoints.md) return 'md';
    if (w >= breakpoints.sm) return 'sm';
    return 'xs';
  });

  return {
    width,
    isMobile,
    isTablet,
    isDesktop,
    breakpoint,
  };
}

/**
 * 媒体查询 Hook
 *
 * @param query - CSS 媒体查询字符串
 * @returns 匹配状态的响应式引用
 *
 * @example
 * ```vue
 * <script setup>
 * import { useMediaQuery } from '@rendora/design/utils/responsive';
 *
 * const { matches } = useMediaQuery('(max-width: 768px)');
 * </script>
 * ```
 */
export function useMediaQuery(query: string) {
  const matches = ref(false);

  if (typeof window === 'undefined') {
    return { matches };
  }

  const mediaQuery = window.matchMedia(query);
  matches.value = mediaQuery.matches;

  const handler = (event: MediaQueryListEvent | MediaQueryList) => {
    matches.value = event.matches;
  };

  // 兼容新旧 API
  if (mediaQuery.addEventListener) {
    onMounted(() => {
      mediaQuery.addEventListener('change', handler);
    });

    onUnmounted(() => {
      mediaQuery.removeEventListener('change', handler);
    });
  } else {
    // 兼容旧浏览器
    onMounted(() => {
      mediaQuery.addListener(handler);
    });

    onUnmounted(() => {
      mediaQuery.removeListener(handler);
    });
  }

  return { matches };
}

/**
 * 预设的媒体查询 Hooks
 */
export const useIsMobile = () => useMediaQuery(`(max-width: ${breakpoints.md - 1}px)`);
export const useIsTablet = () =>
  useMediaQuery(`(min-width: ${breakpoints.md}px) and (max-width: ${breakpoints.lg - 1}px)`);
export const useIsDesktop = () => useMediaQuery(`(min-width: ${breakpoints.lg}px)`);

/**
 * 设置移动端标识到 CSS 变量（用于 CSS 条件判断）
 *
 * @example
 * ```ts
 * // 在应用初始化时调用
 * setMobileFlag();
 * ```
 */
export function setMobileFlag() {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  const isMobileDevice = isMobile();

  root.style.setProperty('--x-is-mobile', isMobileDevice ? '1' : '0');

  // 监听窗口大小变化
  const updateFlag = () => {
    const isMobileDevice = isMobile();
    root.style.setProperty('--x-is-mobile', isMobileDevice ? '1' : '0');
  };

  window.addEventListener('resize', updateFlag);

  // 返回清理函数
  return () => {
    window.removeEventListener('resize', updateFlag);
  };
}
